// One repository: who holds what on it, whether commit trust is switched on,
// what that puts in the public Trust Registry, where the forge has drifted, and
// what happened to it lately.

import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, Check, Minus, Plus, X } from "lucide-react";

import { type Column, DataTable, useSortedRows } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { InfoTip } from "@/components/InfoTip";
import { NamedDid } from "@/components/NamedDid";
import { PageHeader } from "@/components/PageHeader";
import { useSingleAdminMode } from "@/lib/action-badge";
import { useNameBook } from "@/lib/names";
import { useIsGitCommunityAdmin, useViewerDid } from "@/lib/viewer";
import type {
  GitNsDriftItem,
  GitNsNamespaceRow,
  GitNsRepoRow,
  GitNsRight,
  GitNsRightRow,
  GitNsRoleMap,
} from "@/lib/wire-types";

import {
  archiveTask,
  driftAdoptTask,
  driftRevertTask,
  reprojectTask,
  type SignedTask,
} from "./actions";
import {
  fetchAccounts,
  fetchActivity,
  fetchDrift,
  fetchNamespaces,
  fetchProjection,
  fetchRepos,
  fetchRights,
  type ForgeAccounts,
  gitNsKeys,
  indexAccounts,
  memberForAccount,
} from "./api";
import {
  AdoptDialog,
  GrantDialog,
  DriftResolveDialog,
  RevokeDialog,
  TransferDialog,
} from "./dialogs";
import {
  activityVerb,
  BOOTSTRAP_STEP_EXPLAINED,
  bootstrapView,
  commitTrustVerdict,
  countsTowardInvariant,
  desiredTuples,
  expiresWithin,
  forgeRoleFor,
  guardFor,
  inheritedRights,
  isRight,
  isServiceGrant,
  lastCheckOf,
  REPO_RIGHTS,
  repoRights,
  type AdoptStanding,
  adoptStanding,
  projectedRepoRank,
  isAdoptableKind,
  repoStatus,
  revertStanding,
  rightLabel,
  shortName,
  type GitNsSelfGrantWaived,
  type StepState,
  type Tone,
  waiverFor,
} from "./model";
import {
  BreakGlassChip,
  SelfGrantWaivedChip,
  errorMessage,
  readErrorMessage,
  isNotAdministrator,
  formatDay,
  memberPath,
  namespacePath,
  SignTaskDialog,
  ToneChip,
} from "./ui";

type Dialog =
  | { kind: "grant"; initialRight?: "git.repo.own"; title?: string }
  | { kind: "transfer" }
  | { kind: "revoke"; row: GitNsRightRow }
  | { kind: "adopt" }
  | { kind: "drift"; item: GitNsDriftItem; adopt?: { member: string; right: GitNsRight } }
  | { kind: "sign"; task: SignedTask };

const RIGHT_TONE: Record<string, "accent" | "success" | "neutral" | "danger"> = {
  "git.ns.admin": "danger",
  "git.repo.own": "accent",
  "git.repo.maintain": "success",
  "git.commit.sign": "neutral",
};

function ForgeAccountCell({
  did,
  forge,
  forges,
  right,
  roleMap,
}: {
  did: string;
  forge: string;
  forges: ForgeAccounts | undefined;
  right: string;
  /** The repository's role map, when the row is about one repository. */
  roleMap?: GitNsRoleMap | null;
}) {
  const acct = forges?.get(did)?.get(forge);
  const role = roleMap ? forgeRoleFor(roleMap, right) : undefined;
  if (!acct) {
    return (
      <span className="muted">
        Not linked{role === "none" || (!roleMap && right === "git.commit.sign") ? " · fork pull requests" : ""}
      </span>
    );
  }
  return (
    <span title={`${forge} id ${acct.id}`}>
      @{acct.login}
      {role !== undefined && (
        <span className="muted gitns-small" aria-label="Effective forge role">
          {" · "}
          {role === "none" ? "no forge role" : role}
        </span>
      )}
    </span>
  );
}

function GrantedBy({
  row,
  vtcDid,
}: {
  row: GitNsRightRow;
  vtcDid: string | undefined;
}) {
  const book = useNameBook();
  if (row.origin === "roleDerived") {
    return <span className="muted">Role-derived · configuration</span>;
  }
  if (!row.grantedBy) return <span className="muted">—</span>;
  return (
    <span className={row.granterDeparted ? "gitns-warn" : undefined}>
      {row.grantedBy === vtcDid ? (
        "The community"
      ) : (
        <NamedDid did={row.grantedBy} book={book} />
      )}
      {row.granterDeparted && " · departed"}
    </span>
  );
}

type PeopleSortKey = "person" | "right" | "granted";

function PeopleTable({
  rows,
  forge,
  forges,
  onRevoke,
  readOnlyNote,
  vtcDid,
  roleMap,
  waived,
}: {
  rows: GitNsRightRow[];
  vtcDid: string | undefined;
  /** The records single-administrator mode's waiver made, each flagged. */
  waived?: readonly GitNsSelfGrantWaived[];
  forge: string;
  forges: ForgeAccounts | undefined;
  /** The repository's role map: each row then shows the forge role its right
   *  projects to. */
  roleMap?: GitNsRoleMap | null;
  onRevoke?: (row: GitNsRightRow) => void;
  readOnlyNote?: (row: GitNsRightRow) => string | null;
}) {
  const book = useNameBook();
  const sorted = useSortedRows<GitNsRightRow, PeopleSortKey>(
    rows,
    (r, key) => {
      switch (key) {
        case "person":
          return book.nameOf(r.subject) ?? r.subject;
        case "right":
          return rightLabel(r.right);
        case "granted":
          return r.grantedAt ?? null;
      }
    },
    { initialDir: { granted: "desc" } },
  );
  const columns: readonly Column<PeopleSortKey>[] = [
    { key: "person", label: "Person", sortKey: "person" },
    { key: "right", label: "Right", sortKey: "right", className: "gitns-col-right" },
    { key: "forge", label: forge, className: "gitns-col-forge" },
    { key: "granted", label: "Granted by", sortKey: "granted", className: "gitns-col-granted" },
    {
      key: "actions",
      label: <span className="visually-hidden">Actions</span>,
      className: "gitns-col-actions",
    },
  ];
  return (
    <div className="table-scroll">
      <DataTable
        columns={columns}
        sort={sorted.sort}
        onSort={sorted.onSort}
        className="gitns-table gitns-people-table"
      >
        {sorted.rows.map((r) => {
          const note = readOnlyNote?.(r) ?? null;
          const name = book.nameOf(r.subject) ?? r.subject;
          return (
            <tr key={`${r.subject}|${r.right}|${r.origin}`}>
              <td>
                <Link to={memberPath(r.subject)}>
                  <NamedDid did={r.subject} book={book} />
                </Link>
                {!r.subjectMember && (
                  <div>
                    <ToneChip tone="warning" title="Not a member of this community">
                      External signer
                    </ToneChip>
                  </div>
                )}
              </td>
              <td>
                <ToneChip tone={RIGHT_TONE[r.right] ?? "neutral"} title={r.right}>
                  {rightLabel(r.right)}
                </ToneChip>
                {r.breakGlass && (
                  <div>
                    <BreakGlassChip mark={r.breakGlass} />
                  </div>
                )}
                {waiverFor(waived, r) && (
                  <div>
                    <SelfGrantWaivedChip mark={waiverFor(waived, r)} />
                  </div>
                )}
              </td>
              <td>
                <ForgeAccountCell
                  did={r.subject}
                  forge={forge}
                  forges={forges}
                  right={r.right}
                  roleMap={roleMap}
                />
              </td>
              <td>
                <GrantedBy row={r} vtcDid={vtcDid} />
                <div className="muted gitns-small">
                  {r.grantedAt ? formatDay(r.grantedAt) : ""}
                  {r.expiresAt && (
                    <span className={expiresWithin(r, 14) ? "gitns-warn" : undefined}>
                      {r.grantedAt ? " · " : ""}expires {formatDay(r.expiresAt)}
                    </span>
                  )}
                  {r.granterDeparted && " · review"}
                </div>
                {r.reason && <div className="muted gitns-small">“{r.reason}”</div>}
                {r.breakGlass && (
                  <div className="muted gitns-small gitns-justification">
                    Break-glass: “{r.breakGlass.justification}”
                  </div>
                )}
              </td>
              <td>
                {note ? (
                  <span className="muted gitns-small">{note}</span>
                ) : (
                  onRevoke && (
                    <button
                      type="button"
                      className="secondary sm destructive"
                      aria-label={`Revoke ${rightLabel(r.right)} from ${name}`}
                      onClick={() => onRevoke(r)}
                    >
                      Revoke
                    </button>
                  )
                )}
              </td>
            </tr>
          );
        })}
      </DataTable>
    </div>
  );
}

const DRIFT_LABEL: Record<GitNsDriftItem["type"], string> = {
  roleAdded: "Role added on the forge",
  roleRemoved: "Projected role missing",
  roleChanged: "Role changed on the forge",
  requiredCheckMissing: "Required check no longer required",
  protectionWeakened: "Protection weakened",
  bootstrapMissing: "Bootstrap file or variable gone",
};

function DriftList({
  items,
  forges,
  ns,
  repo,
  rights,
  onAdopt,
  onRevert,
}: {
  items: GitNsDriftItem[];
  forges: ForgeAccounts | undefined;
  ns: GitNsNamespaceRow;
  repo: GitNsRepoRow;
  /** `null` until the rights listing has answered: adopting a `roleChanged`
   *  depends on what the member already holds, so nothing is offered until
   *  that is known. */
  rights: GitNsRightRow[] | null;
  onAdopt: (item: GitNsDriftItem, member: string, right: GitNsRight) => void;
  onRevert: (item: GitNsDriftItem) => void;
}) {
  const book = useNameBook();
  const viewer = useViewerDid();
  const superAdmin = useIsGitCommunityAdmin();
  const singleAdmin = useSingleAdminMode();
  return (
    <ul className="finding-list">
      {items.map((d, i) => {
        const member =
          d.account && forges ? memberForAccount(forges, d.account.forge, d.account.id) : undefined;
        const adopt: AdoptStanding | null = !isAdoptableKind(d)
          ? null
          : rights === null
            ? {
                may: false,
                handOver: false,
                why: "Reading who holds what here before offering to adopt…",
              }
            : adoptStanding(
                viewer,
                superAdmin,
                ns,
                repo,
                d,
                member,
                member ? projectedRepoRank(rights, member, repo, ns) : 0,
                singleAdmin,
              );
        const protection = d.type === "requiredCheckMissing" || d.type === "protectionWeakened";
        const standing = revertStanding(viewer, superAdmin, ns, repo, d);
        const label = DRIFT_LABEL[d.type] ?? d.type;
        return (
          <li key={i} className={`finding ${protection ? "error" : "warn"}`}>
            <strong>{label}</strong>
            <span>
              {d.account && (
                <>
                  @{d.account.login}
                  {member ? (
                    <>
                      {" — linked by "}
                      {book.nameOf(member) && <b>{book.nameOf(member)} </b>}
                      <code className="gitns-party-did">{member}</code>
                    </>
                  ) : (
                    " — linked by no member"
                  )}
                  {" · "}
                </>
              )}
              {d.observed ? `forge shows ${d.observed}` : "forge shows nothing"}
              {" · "}
              {d.expected ? `projection calls for ${d.expected}` : "projection calls for nothing"}
            </span>
            {protection && (
              <span className="muted">
                A weakened ruleset silently removes the guarantee, so the bridge
                re-applies it by default (rulesets enforce).
              </span>
            )}
            <span className="gitns-drift-actions">
              {standing.may ? (
                <button
                  type="button"
                  className="secondary sm"
                  aria-label={`Revert: ${label}${d.account ? ` @${d.account.login}` : ""}`}
                  onClick={() => onRevert(d)}
                >
                  Revert to the VTC's state
                </button>
              ) : (
                <span className="muted">
                  {standing.why}
                  {ns.mode === "bridge" && (
                    <>
                      {" "}
                      <code aria-label="Revert command">
                        {driftRevertTask(repo.resource, d, undefined, repo.roleMap).command}
                      </code>
                    </>
                  )}
                </span>
              )}
              {adopt?.may && (
                <button
                  type="button"
                  className="secondary sm"
                  aria-label={`Adopt: ${label}${d.account ? ` @${d.account.login}` : ""}`}
                  onClick={() => onAdopt(d, adopt.member, adopt.right)}
                >
                  Adopt into VTC as {rightLabel(adopt.right).toLowerCase()}
                </button>
              )}
              {adopt && !adopt.may && (
                <span className="muted">
                  {adopt.why}
                  {adopt.handOver && (
                    <>
                      {" "}
                      <code aria-label="Adopt command">
                        {driftAdoptTask(repo.resource, d, adopt.member, adopt.right).command}
                      </code>
                    </>
                  )}
                </span>
              )}
              {d.type === "roleAdded" && ns.roleDrift !== "enforce" && (
                <span className="muted">
                  Or set <code>role_drift = "enforce"</code> in the git namespace policy and
                  the bridge re-projects roles itself.
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** The repository's history, from the namespace's activity feed. */
function Activity({ ns, resource }: { ns: GitNsNamespaceRow; resource: string }) {
  const book = useNameBook();
  const q = useQuery({
    queryKey: gitNsKeys.activity(ns.id),
    queryFn: () => fetchActivity(ns.id),
  });
  const items = (q.data?.items ?? []).filter((i) => i.resource === resource).slice(0, 10);
  const who = (did: string | null | undefined) =>
    did ? <NamedDid did={did} book={book} nameOnly={!!book.nameOf(did)} /> : null;

  return (
    <section className="card" aria-labelledby="gitns-activity">
      <h3 id="gitns-activity">Recent activity</h3>
      {q.isPending && <p>Loading activity…</p>}
      {q.isError &&
        (isNotAdministrator(q.error) ? (
          <p className="muted">
            Activity is shown to this namespace's admins, and this session's DID does
            not hold <code>git.ns.admin</code> on {ns.resource}.
          </p>
        ) : (
          <p className="muted">Activity could not be read: {errorMessage(q.error)}.</p>
        ))}
      {q.isSuccess && items.length === 0 && (
        <EmptyState compact title="Nothing recorded for this repository yet." />
      )}
      {items.length > 0 && (
        <ul className="gitns-activity">
          {items.map((it, i) => (
            <li key={i}>
              <time dateTime={it.occurredAt} className="muted">
                {formatDay(it.occurredAt)}
              </time>{" "}
              · {who(it.actor)}
              {it.actor && " "}
              {activityVerb(it.action)}
              {it.right && (
                <>
                  {" "}
                  <b>{rightLabel(it.right).toLowerCase()}</b>
                </>
              )}
              {it.subject && it.subject !== it.actor && <> — {who(it.subject)}</>}
              {it.detail && <span className="muted"> ({it.detail})</span>}
            </li>
          ))}
        </ul>
      )}
      <p className="muted gitns-small">
        Rights changes, drift and bridge jobs. The full record is in the Audit trail.
      </p>
    </section>
  );
}

const OUTCOME_TONE: Record<string, "success" | "neutral" | "danger" | "accent"> = {
  applied: "success",
  unchanged: "neutral",
  failed: "danger",
  skipped: "accent",
};

const STEP_CHIP: Record<StepState, { tone: Tone; label: string }> = {
  done: { tone: "success", label: "in place" },
  missing: { tone: "warning", label: "missing" },
  failed: { tone: "danger", label: "failed" },
  notApplicable: { tone: "neutral", label: "not applicable" },
};

const STEP_ICON: Record<StepState, ReactNode> = {
  done: <Check aria-hidden="true" size={16} />,
  missing: <X aria-hidden="true" size={16} />,
  failed: <AlertTriangle aria-hidden="true" size={16} />,
  notApplicable: <Minus aria-hidden="true" size={16} />,
};

const VERDICT_CLASS: Record<Tone, string> = {
  success: "ok",
  accent: "",
  neutral: "",
  warning: "warn",
  danger: "error",
};

/**
 * Commit trust on one repository, answered first and explained after: a
 * verdict (enforced, enforced with a caveat, or not — and what to do), then
 * each setup step with whether it applies here and why, then what stops a
 * pull request from passing its own check, then the latest check result.
 */
function CommitTrust({ ns, repo }: { ns: GitNsNamespaceRow; repo: GitNsRepoRow }) {
  const guard = guardFor(ns, repo);
  const check = lastCheckOf(repo);
  const verdict = commitTrustVerdict(ns, repo);
  const steps = bootstrapView(ns, repo);
  return (
    <section className="card" aria-labelledby="gitns-trust">
      <div className="gitns-section-head">
        <h3 id="gitns-trust">Commit trust on {ns.forge}</h3>
        <InfoTip label="About commit trust">
          Commit trust means the forge refuses to merge a pull request unless every commit
          in it is signed by a DID holding git.commit.sign on this repository in the Trust
          Registry. The bridge sets it up; this card says whether it is in force.
        </InfoTip>
      </div>

      <div className={`finding gitns-verdict ${VERDICT_CLASS[verdict.tone]}`} role="status">
        <strong>{verdict.headline}</strong>
        <span>{verdict.detail}</span>
      </div>

      <div>
        <span className="gitns-caption">
          Setup steps
          <InfoTip label="About setup steps">
            What the bridge puts on the repository. Not every repository uses all four:
            which apply depends on how the check runs (the guard below) and on the forge. A
            step marked not applicable is neither missing nor a problem.
          </InfoTip>
        </span>
        <ul className="gitns-checklist">
          {steps.map((s) => (
            <li key={s.key} className={s.state}>
              {STEP_ICON[s.state]}
              <span>
                <b>{s.label}</b>{" "}
                <ToneChip tone={STEP_CHIP[s.state].tone}>{STEP_CHIP[s.state].label}</ToneChip>
                <InfoTip label={`About ${s.label.toLowerCase()}`}>
                  {BOOTSTRAP_STEP_EXPLAINED[s.key]}
                </InfoTip>
                <span className="muted gitns-small gitns-block">
                  {s.state === "done" ? s.detail : s.why}
                </span>
              </span>
            </li>
          ))}
        </ul>
      </div>

      {repo.failedStep && (
        <div className="finding error">
          <strong>Failed at: {repo.failedStep}</strong>
          {repo.lastError && <span>{repo.lastError}</span>}
          <span className="muted">Every step is check-then-apply, so a retry is safe.</span>
        </div>
      )}

      <div
        className={`finding ${guard.tone === "danger" ? "error" : guard.tone === "warning" ? "warn" : guard.tone === "success" ? "ok" : ""}`}
      >
        <strong>
          Guard: {guard.label}
          <InfoTip label="About the guard">
            A pull request's own workflow runs from its own files, so without a guard anyone
            with write access could edit the check to pass. The guard is what stops that.
          </InfoTip>
        </strong>
        <span>{guard.detail}</span>
        <span className="muted gitns-small">
          {guard.source === "reported"
            ? "As the bridge last reported it."
            : `Expected for a ${ns.mode}-mode ${ns.kind ?? "namespace"} (design §9); the bridge has not reported the guard in force.`}
        </span>
      </div>

      <p className="gitns-small">
        Latest check:{" "}
        {check ? (
          <>
            <ToneChip
              tone={check.conclusion === "success" ? "success" : check.conclusion === "failure" ? "danger" : "neutral"}
            >
              {check.conclusion}
            </ToneChip>
            {check.concludedAt && <> {formatDay(check.concludedAt)}</>}
            {check.sha && (
              <>
                {" "}
                on <code>{check.sha.slice(0, 12)}</code>
              </>
            )}
          </>
        ) : (
          <span className="muted">none reported</span>
        )}
        <InfoTip label="About the latest check">
          The verify-trust result on the most recent pull request the bridge saw. A failure
          usually means it did its job — it refused an untrusted commit — not that the setup
          is broken.
        </InfoTip>
      </p>

      {repo.steps.length > 0 && (
        <details className="gitns-last-run">
          <summary className="gitns-small">The bridge's last run on this repository</summary>
          <ol className="gitns-step-outcomes">
            {repo.steps.map((s, i) => (
              <li key={`${s.step}-${i}`}>
                <span className="gitns-mono">{s.step}</span>{" "}
                <ToneChip tone={OUTCOME_TONE[s.outcome] ?? "neutral"}>{s.outcome}</ToneChip>
                {s.detail && <span className="muted gitns-small"> {s.detail}</span>}
              </li>
            ))}
          </ol>
          <p className="muted gitns-small">
            What its last create, bootstrap or inspection reported: <i>applied</i> changed
            something, <i>unchanged</i> found it already right, <i>skipped</i> means the step
            does not apply here. A step it did not report is not part of this repository's
            setup.
          </p>
        </details>
      )}
    </section>
  );
}

/** The registry records this repository's rights call for — its own, and the
 *  namespace-wide commit rights that also cover it — each marked published or
 *  not. Shared by the summary strip and the registry section; react-query
 *  reads the projection once for both. */
function useRegistryTuples(
  resource: string,
  nsResource: string | undefined,
  repos: GitNsRepoRow[],
  namespaces: GitNsNamespaceRow[],
  rights: GitNsRightRow[] | null,
) {
  const proj = useQuery({ queryKey: gitNsKeys.projection, queryFn: fetchProjection });
  const tuples = useMemo(
    () =>
      desiredTuples(rights ?? [], namespaces, repos, proj.data?.published ?? [])
        .filter(
          (t) =>
            t.resource === resource || (t.resource === nsResource && t.action === "git.commit.sign"),
        )
        // A person's records together, this repository's before the namespace's.
        .sort(
          (a, b) =>
            a.entity.localeCompare(b.entity) ||
            Number(a.resource !== resource) - Number(b.resource !== resource),
        ),
    [rights, namespaces, repos, proj.data, resource, nsResource],
  );
  return { proj, tuples };
}

type SummaryTone = "ok" | "warn" | "bad" | "neutral";

const VERDICT_SUMMARY_TONE: Record<Tone, SummaryTone> = {
  success: "ok",
  accent: "neutral",
  neutral: "neutral",
  warning: "warn",
  danger: "bad",
};

/** One figure in the strip under the heading: a link to the section that
 *  explains it, and an (i) saying what the figure means. */
function SummaryTile({
  label,
  value,
  detail,
  tone = "neutral",
  href,
  tip,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: SummaryTone;
  href: string;
  tip: string;
}) {
  return (
    <div className={`gitns-summary-tile ${tone}`}>
      <span className="gitns-summary-label">
        {label}
        <InfoTip label={`About ${label.toLowerCase()}`} side="bottom">
          {tip}
        </InfoTip>
      </span>
      <a className="gitns-summary-value" href={href} aria-label={`${label}: ${value}. Go to the section`}>
        {value}
      </a>
      {detail && <span className="gitns-summary-detail">{detail}</span>}
    </div>
  );
}

/** Who holds what, whether commit trust is in force, what is published and
 *  whether the forge has drifted — at a glance, before the detail. */
function RepoSummary({
  ns,
  people,
  repo,
  drift,
  tuples,
  projectionRead,
  rightsRead,
}: {
  ns: GitNsNamespaceRow;
  people: GitNsRightRow[];
  repo: GitNsRepoRow;
  drift: number;
  tuples: { published: boolean }[];
  projectionRead: boolean;
  rightsRead: boolean;
}) {
  const holders = (right: GitNsRight) =>
    new Set(people.filter((r) => r.right === right).map((r) => r.subject)).size;
  const steps = bootstrapView(ns, repo);
  const applicable = steps.filter((s) => s.state !== "notApplicable");
  const done = applicable.filter((s) => s.state === "done").length;
  const verdict = commitTrustVerdict(ns, repo);
  const published = tuples.filter((t) => t.published).length;
  const rightsValue = (right: GitNsRight) => (rightsRead ? String(holders(right)) : "…");
  const trustValue =
    verdict.tone === "success" || verdict.tone === "accent"
      ? "Enforced"
      : verdict.tone === "danger"
        ? "Not enforced"
        : "Needs action";
  return (
    <div className="gitns-summary" role="group" aria-label="Summary">
      <SummaryTile
        label="Owners"
        value={rightsValue("git.repo.own")}
        tone={rightsRead && holders("git.repo.own") === 0 ? "bad" : "neutral"}
        href="#gitns-people"
        tip="People holding git.repo.own here. Owners grant owner, maintainer and committer on the repository; one with no expiry must always remain."
      />
      <SummaryTile
        label="Maintainers"
        value={rightsValue("git.repo.maintain")}
        href="#gitns-people"
        tip="People holding git.repo.maintain here: they may merge and get the forge's maintain role, but cannot grant rights."
      />
      <SummaryTile
        label="Committers"
        value={rightsValue("git.commit.sign")}
        href="#gitns-people"
        tip="People granted git.commit.sign on this repository itself. Owners and maintainers may commit too, as may anyone with a commit right on the whole namespace — see Through the namespace."
      />
      <SummaryTile
        label="Commit trust"
        value={trustValue}
        detail={`${done} of ${applicable.length} steps in place${steps.length > applicable.length ? ` · ${steps.length - applicable.length} not applicable` : ""}`}
        tone={VERDICT_SUMMARY_TONE[verdict.tone]}
        href="#gitns-trust"
        tip={`Whether the forge refuses to merge commits not signed by an authorized DID. ${verdict.headline}.`}
      />
      <SummaryTile
        label="Registry"
        value={rightsRead && projectionRead ? `${published}/${tuples.length}` : "…"}
        detail={
          rightsRead && projectionRead
            ? published === tuples.length
              ? "all published"
              : `${tuples.length - published} pending`
            : undefined
        }
        tone={rightsRead && projectionRead && published < tuples.length ? "warn" : "neutral"}
        href="#gitns-registry"
        tip="The public Trust Registry records these rights call for, and how many are published. The commit-trust check reads these records, so a pending one is a right that does not work yet."
      />
      <SummaryTile
        label="Drift"
        value={String(drift)}
        detail={drift === 0 ? (repo.syncState === "drift" ? "reported" : "in sync") : "to resolve"}
        tone={drift > 0 || repo.syncState === "drift" ? "bad" : "ok"}
        href={drift > 0 || repo.syncState === "drift" ? "#drift" : "#gitns-trust"}
        tip="Changes made on the forge directly that differ from what the VTC projected — a role someone added, or protection someone weakened. Zero means the forge matches."
      />
    </div>
  );
}

type TupleSortKey = "entity" | "action" | "resource" | "state";

const TUPLE_COLUMNS: readonly Column<TupleSortKey>[] = [
  { key: "entity", label: "Entity", sortKey: "entity" },
  { key: "action", label: "Action", sortKey: "action", className: "gitns-col-action" },
  { key: "resource", label: "Resource", sortKey: "resource", className: "gitns-col-resource" },
  { key: "state", label: "State", sortKey: "state", className: "gitns-col-state" },
];

function RegistryPreview({
  resource,
  rights,
  rightsError,
  proj,
  tuples,
}: {
  resource: string;
  /** `null` until the rights are read; the preview is built from them, so
   *  without them it says nothing about what is or is not published. */
  rights: GitNsRightRow[] | null;
  rightsError: unknown;
} & ReturnType<typeof useRegistryTuples>) {
  const book = useNameBook();
  const pending = tuples.filter((t) => !t.published).length;
  const sortedTuples = useSortedRows<(typeof tuples)[number], TupleSortKey>(tuples, (t, key) => {
    switch (key) {
      case "entity":
        return book.nameOf(t.entity) ?? t.entity;
      case "action":
        return t.action;
      case "resource":
        return t.resource;
      case "state":
        return proj.data ? (t.published ? "Published" : "Pending") : null;
    }
  });

  return (
    <section className="card" aria-labelledby="gitns-registry">
      <div className="gitns-section-head">
        <h3 id="gitns-registry">Published to the Trust Registry</h3>
        <ToneChip tone="warning">Public</ToneChip>
      </div>
      {proj.isError && (
        <p className="muted">
          What is published could not be read ({readErrorMessage(proj.error)}); the list
          below is what the records call for.
        </p>
      )}
      {proj.data && !proj.data.registryConfigured && (
        <p className="muted">
          No Trust Registry is configured, so nothing is being published. These are the
          records that would be.
        </p>
      )}
      {rights === null ? (
        rightsError ? (
          <p className="muted">
            The rights could not be read ({readErrorMessage(rightsError)}), so what they
            publish cannot be shown.
          </p>
        ) : (
          <p>Loading…</p>
        )
      ) : tuples.length === 0 ? (
        <EmptyState compact title="Nothing on this repository is published." />
      ) : (
        <div className="table-scroll">
          <DataTable
            columns={TUPLE_COLUMNS}
            sort={sortedTuples.sort}
            onSort={sortedTuples.onSort}
            caption={`Registry records for ${resource}`}
            className="gitns-table gitns-tuples"
          >
            {sortedTuples.rows.map((t) => (
              <tr key={`${t.entity}|${t.action}|${t.resource}`}>
                <td>
                  <NamedDid did={t.entity} book={book} />
                </td>
                <td>
                  <code>{t.action}</code>
                  {t.impliedBy && (
                    <div className="muted gitns-small">implied by {t.impliedBy}</div>
                  )}
                </td>
                <td>
                  <code>{t.resource}</code>
                </td>
                <td>
                  {proj.data ? (
                    <ToneChip tone={t.published ? "success" : "accent"}>
                      {t.published ? "Published" : "Pending"}
                    </ToneChip>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
          </DataTable>
        </div>
      )}
      <p className="muted gitns-small">
        The CI check reads only <code>git.commit.sign</code>; the projector writes each
        owner's and maintainer's implied commit right explicitly. Anyone can query who
        owns or commits to this repository. Reasons are never published.
        {pending > 0 && proj.data?.registryConfigured && ` ${pending} not yet published.`}{" "}
        <Link to="/recognition">Every record this community publishes</Link> is listed,
        searchable, on the Recognition page.
      </p>
    </section>
  );
}

export function RepoDetail() {
  const params = useParams();
  const resource = decodeURIComponent(params.resource ?? "");
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const book = useNameBook();

  const nsQ = useQuery({ queryKey: gitNsKeys.namespaces, queryFn: fetchNamespaces });
  const reposQ = useQuery({ queryKey: gitNsKeys.repos, queryFn: fetchRepos });
  const rightsQ = useQuery({ queryKey: gitNsKeys.rights, queryFn: fetchRights });
  const driftQ = useQuery({ queryKey: gitNsKeys.drift, queryFn: fetchDrift });
  const accountsQ = useQuery({ queryKey: gitNsKeys.accounts, queryFn: fetchAccounts });
  const forges = useMemo(() => indexAccounts(accountsQ.data), [accountsQ.data]);

  const repos = reposQ.data?.repos ?? [];
  const namespaces = nsQ.data?.namespaces ?? [];
  const allRights = rightsQ.data?.rights ?? [];
  const repo = repos.find((r) => r.resource === resource);
  const ns = repo ? namespaces.find((n) => n.id === repo.namespace) : undefined;
  const registry = useRegistryTuples(
    resource,
    ns?.resource,
    repos,
    namespaces,
    rightsQ.isSuccess ? allRights : null,
  );

  const leaf = resource.split("/").pop();
  const trail = [
    ns
      ? { label: ns.resource, to: namespacePath(ns.id) }
      : { label: resource.split("/").slice(0, 2).join("/") },
    { label: leaf },
  ];
  const breadcrumb = (
    <PageHeader trail={trail} title={<span className="gitns-mono">{leaf}</span>} />
  );

  if (reposQ.isPending || nsQ.isPending) {
    return (
      <>
        {breadcrumb}
        <section className="card">
          <p>Loading {resource}…</p>
        </section>
      </>
    );
  }
  if (reposQ.isError || nsQ.isError) {
    return (
      <>
        {breadcrumb}
        <section className="card error">
          <h3>{resource} could not be read</h3>
          <p>{errorMessage(reposQ.error ?? nsQ.error)}</p>
        </section>
      </>
    );
  }
  if (!repo) {
    return (
      <>
        {breadcrumb}
        <section className="card">
          <p className="muted">
            The VTC records no repository at <code>{resource}</code>. It may have been
            renamed, or never adopted.
          </p>
        </section>
      </>
    );
  }
  if (!ns || repo.state === "detached") {
    // Detached: its namespace was unbound (and may be gone from the list), or
    // it moved outside it. The record stays, for history; nothing is governed
    // or published, so none of the governed panels apply.
    return (
      <>
        <PageHeader
          trail={trail}
          title={<span className="gitns-mono">{repo.resource}</span>}
        />
        <div className="gitns-chips">
          <ToneChip tone="neutral">Detached</ToneChip>
          <span className="muted gitns-small">
            {repo.forgeId && `forge id ${repo.forgeId} · `}recorded {formatDay(repo.createdAt)}
          </span>
        </div>
        <section className="card">
          <p>
            No longer governed: {ns ? "it moved outside its namespace" : "its namespace was unbound"}.
            Its rights were revoked and withdrawn from the Trust Registry, and nothing
            about it is published. To govern it again, bind the namespace it is in now
            and adopt it there.
          </p>
        </section>
      </>
    );
  }

  const status = repoStatus(repo);
  const people = repoRights(allRights, repo.resource);
  const inherited = inheritedRights(allRights, ns);
  const drift = driftQ.data?.repos.find((d) => d.resource === repo.resource)?.drift ?? [];
  const owners = repo.owners;
  // The service grant is the one record the community issues as itself, so its
  // granter is this VTC's DID — which lets "granted by" say so in words.
  const vtcDid = inherited.find((r) => isServiceGrant(r, ns))?.grantedBy ?? undefined;
  const governed = repo.state !== "unmanaged" && repo.state !== "detached";
  const archived = repo.state === "archived";
  // Fixed rule 3 of `git-ns/right/grant` 0.3 counts only owners with no
  // expiry that are not an unratified break-glass: revoking one of those is
  // never refused, and the last one of the rest is.
  const countingOwners = people.filter(
    (r) => r.right === "git.repo.own" && r.origin === "recorded" && countsTowardInvariant(r),
  );
  const reprojectable =
    ns.state === "bound" &&
    !ns.installationRemoved &&
    (repo.state === "active" || repo.state === "orphaned");
  const lastOwner = (r: GitNsRightRow) =>
    r.right === "git.repo.own" &&
    countsTowardInvariant(r) &&
    countingOwners.length === 1 &&
    countingOwners[0]!.subject === r.subject
      ? "Last owner — name another first"
      : null;

  return (
    <>
      <PageHeader
        trail={trail}
        title={<span className="gitns-mono">{shortName(repo.resource)}</span>}
        actions={
          <>
            {repo.state === "unmanaged" ? (
              <button type="button" className="primary" onClick={() => setDialog({ kind: "adopt" })}>
                Adopt
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="secondary"
                  disabled={!governed || archived}
                  onClick={() => setDialog({ kind: "transfer" })}
                >
                  Transfer ownership
                </button>
                {ns.mode === "bridge" && (
                  <button
                    type="button"
                    className="secondary"
                    disabled={!reprojectable}
                    title="Have the bridge re-apply this repository's forge roles under its current role map. No right changes."
                    onClick={() => setDialog({ kind: "sign", task: reprojectTask(repo.resource) })}
                  >
                    Re-project roles
                  </button>
                )}
                <button
                  type="button"
                  className="secondary destructive"
                  disabled={!governed || archived}
                  onClick={() => setDialog({ kind: "sign", task: archiveTask(repo.resource) })}
                >
                  Archive
                </button>
              </>
            )}
          </>
        }
      />
      <div className="gitns-chips">
        <ToneChip tone="neutral">{repo.visibility}</ToneChip>
        <ToneChip tone={status.tone}>{status.label}</ToneChip>
        {repo.bootstrap.requiredCheck && (
          <ToneChip tone="accent">“Verify commit trust” required</ToneChip>
        )}
        <span className="muted gitns-small">
          {repo.forgeId && `${ns.forge} id ${repo.forgeId} · `}
          recorded {formatDay(repo.createdAt)}
        </span>
      </div>

      {repo.roleMapStale && repo.roleMap && (
        <div className="finding warn">
          <strong>Forge roles projected under an earlier role map</strong>
          <span>
            The bridge now gives owners {repo.roleMap.own}, maintainers {repo.roleMap.maintain} and
            committers {repo.roleMap.commit === "none" ? "no role" : repo.roleMap.commit} here. The
            VTC has queued a re-projection; this clears once the bridge confirms it.
          </span>
          {reprojectable && (
            <span>
              <button
                type="button"
                className="secondary sm"
                onClick={() => setDialog({ kind: "sign", task: reprojectTask(repo.resource) })}
              >
                Re-project now
              </button>
            </span>
          )}
        </div>
      )}

      {repo.state === "orphaned" && (
        <div className="finding error">
          <strong>Orphaned</strong>
          <span>
            Its last owner left the community, so ownership passed to the namespace
            admins. Name a new owner.
          </span>
          <span>
            <button
              type="button"
              className="primary sm"
              onClick={() =>
                setDialog({
                  kind: "grant",
                  initialRight: "git.repo.own",
                  title: `Assign an owner to ${shortName(repo.resource)}`,
                })
              }
            >
              Assign owner
            </button>
          </span>
        </div>
      )}

      <RepoSummary
        ns={ns}
        people={people}
        repo={repo}
        drift={drift.length}
        tuples={registry.tuples}
        projectionRead={registry.proj.isSuccess}
        rightsRead={rightsQ.isSuccess}
      />

      <div className="gitns-detail">
        {(drift.length > 0 || repo.syncState === "drift") && (
          <section className="card" id="drift" aria-labelledby="gitns-drift">
            <h3 id="gitns-drift">Drift</h3>
            {driftQ.isError && (
              <p className="muted">Drift could not be read: {readErrorMessage(driftQ.error)}.</p>
            )}
            <DriftList
              items={drift}
              forges={forges}
              ns={ns}
              repo={repo}
              rights={rightsQ.isSuccess ? allRights : null}
              onRevert={(item) => setDialog({ kind: "drift", item })}
              onAdopt={(item, member, right) =>
                setDialog({ kind: "drift", item, adopt: { member, right } })
              }
            />
          </section>
        )}

        <section className="card" aria-labelledby="gitns-people">
          <div className="gitns-section-head">
            <h3 id="gitns-people">People and rights</h3>
            <button
              type="button"
              className="primary sm"
              disabled={!governed || archived}
              onClick={() => setDialog({ kind: "grant" })}
            >
              <Plus aria-hidden="true" size={14} /> Add person
            </button>
          </div>
          {rightsQ.isPending && <p>Loading rights…</p>}
          {rightsQ.isError && (
            <p className="muted">Rights could not be read: {readErrorMessage(rightsQ.error)}.</p>
          )}
          {rightsQ.isSuccess && people.length === 0 && (
            <p className="muted">
              {repo.state === "unmanaged"
                ? "Nobody holds a right here: adopt it to name an owner."
                : "No right is recorded on this repository itself."}
            </p>
          )}
          {people.length > 0 && (
            <PeopleTable
              rows={people}
              forge={ns.forge}
              forges={forges}
              vtcDid={vtcDid}
              roleMap={repo.roleMap}
              waived={driftQ.data?.waived}
              readOnlyNote={(r) =>
                r.origin === "roleDerived"
                  ? "Managed in configuration"
                  : !isRight(r.right)
                    ? "Unknown right"
                    : lastOwner(r)
              }
              onRevoke={(r) => setDialog({ kind: "revoke", row: r })}
            />
          )}
          <p className="muted gitns-small">
            Owners can grant owner, maintainer and committer on this repository.
            Committers need no forge access; they contribute through fork pull
            requests.
          </p>
        </section>

        {inherited.length > 0 && (
          <section className="card" aria-labelledby="gitns-inherited">
            <h3 id="gitns-inherited">Through the namespace</h3>
            <p className="muted gitns-small">
              Rights on {ns.resource} that reach this repository. Change them from the
              namespace. <code>git.ns.admin</code> gives no role on the forge; a
              namespace-level <code>git.commit.sign</code> is projected here as a committer
              right on this repository would be.
            </p>
            <PeopleTable
              rows={inherited}
              forge={ns.forge}
              forges={forges}
              vtcDid={vtcDid}
              roleMap={repo.roleMap}
              waived={driftQ.data?.waived}
              readOnlyNote={(r) =>
                isServiceGrant(r, ns)
                  ? "Bridge service grant · Dependabot re-sign"
                  : "On the namespace"
              }
            />
          </section>
        )}

        <div className="gitns-pair">
          <CommitTrust ns={ns} repo={repo} />
          <Activity ns={ns} resource={repo.resource} />
        </div>

        <RegistryPreview
          resource={repo.resource}
          rights={rightsQ.isSuccess ? allRights : null}
          rightsError={rightsQ.error}
          {...registry}
        />
      </div>

      {dialog?.kind === "grant" && (
        <GrantDialog
          resource={repo.resource}
          rights={dialog.initialRight ? [dialog.initialRight] : REPO_RIGHTS}
          initialRight={dialog.initialRight}
          title={dialog.title}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "revoke" && (
        <RevokeDialog
          subject={dialog.row.subject}
          subjectName={book.nameOf(dialog.row.subject) ?? undefined}
          right={dialog.row.right as GitNsRight}
          resource={dialog.row.resource}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "transfer" && (
        <TransferDialog
          resource={repo.resource}
          owners={owners}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "adopt" && (
        <AdoptDialog
          resource={repo.resource}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "drift" && (
        <DriftResolveDialog
          resource={repo.resource}
          roleMap={repo.roleMap}
          item={dialog.item}
          adopt={dialog.adopt}
          label={DRIFT_LABEL[dialog.item.type] ?? dialog.item.type}
          onClose={() => setDialog(null)}
          onBuilt={(task) => setDialog({ kind: "sign", task })}
        />
      )}
      {dialog?.kind === "sign" && (
        <SignTaskDialog task={dialog.task} onClose={() => setDialog(null)} />
      )}
    </>
  );
}
